import Swal from "sweetalert2";
import { CombinedError } from "@urql/svelte";

function getMessageFromError(error: unknown): string {
    if (error instanceof CombinedError && error.graphQLErrors.length > 0) {
        return error.graphQLErrors.map((e) => e.message).join("\n");
    } else if (error instanceof Error) {
        return error.message;
    } else if (typeof error === "string") {
        return error;
    } else {
        return "Unknown error";
    }
}
export function displayErrorPopup(e: unknown) {
    console.error(e);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Swal.fire({
        type: "error",
        title: "Oops...",
        text: "An error occurred: " + getMessageFromError(e),
    });
}

export type SlugifyOptions = {
    /** max code points (not bytes). default: 120 */
    maxLen?: number;
    /** keep original case. default: false (lowercase) */
    keepCase?: boolean;
    /** return a URL-encoded slug. default: true */
    encode?: boolean;
};

/**
 * Unicode-safe slugify:
 * - keeps letters from any script and digits
 * - converts spaces/punct/control to hyphens
 * - removes everything else
 * - collapses/trim hyphens, lowercases, NFC-normalizes
 * - optionally URL-encodes the result (percent-encoding, for path segments)
 */
export function slugify(input: string | null | undefined, options: SlugifyOptions = {}): string {
    const { maxLen = 120, keepCase = false, encode = true } = options;
    if (input == null) return "";

    // 1) normalize to NFC (stable composed form)
    let s = input.normalize("NFC");

    // 2) turn any whitespace, punctuation, or control into a hyphen
    //    (this removes '/', '?', '#', '&', etc.)
    s = s.replace(/[\p{Z}\p{P}\p{C}]+/gu, "-");

    // 3) keep only letters (any script), numbers, and hyphen
    s = s.replace(/[^-\p{L}\p{N}]+/gu, "");

    // 4) collapse/trim hyphens
    s = s.replace(/-+/g, "-").replace(/^-|-$/g, "");

    // 5) lowercasing (unicode-aware)
    if (!keepCase) s = s.toLocaleLowerCase("und");

    // 6) enforce length by code points (avoid splitting surrogate pairs)
    if (maxLen > 0) {
        const cp = Array.from(s);
        if (cp.length > maxLen) {
            s = cp.slice(0, maxLen).join("").replace(/-+$/g, "");
        }
    }

    // 7) URL-encode for safe use in a path segment
    return encode ? encodeURIComponent(s) : s;
}
